import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Agent, ChatMessage, UserInfo } from '../../types';
import { LANG_FLAGS, LANG_NAMES, CLAUDE_API } from '../../constants';
import { Avatar } from '../common/Avatar';
import { translateMessage } from '../../services/translation';
import s from './ChatPanel.module.css';

const fmt = (d: Date) =>
  d.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const uid = () => Math.random().toString(36).slice(2, 10);

const CLAUDE_MODEL = 'claude-sonnet-4-5';

// ── Training match panel ──────────────────────────────────────────────────────

interface TrainingMatch {
  id: string;
  title: string;
  tags: string[];
  durationSec: number;
  sequenceId?: string;
}

interface GrammarResult {
  original: string;
  corrected: string;
  translated: string;
  backTranslated: string;
  meaningMatch: boolean;
}

// ── Grammar + back-translate using Claude ─────────────────────────────────────

async function grammarAndTranslate(
  text: string,
  targetLang: string,
  apiKey: string,
): Promise<GrammarResult> {
  const langName = LANG_NAMES[targetLang] ?? targetLang;

  const resp = await fetch(CLAUDE_API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: CLAUDE_MODEL,
      max_tokens: 600,
      messages: [{
        role: 'user',
        content:
          `You are a grammar checker and translator for a customer support agent.\n` +
          `Do THREE things and respond with ONLY valid JSON, no markdown:\n` +
          `1. Correct any grammar/spelling errors in the English text\n` +
          `2. Translate the corrected text to ${langName}\n` +
          `3. Back-translate the ${langName} text back to English\n\n` +
          `Return JSON: {"corrected":"...","translated":"...","backTranslated":"..."}\n\n` +
          `English text: ${text}`,
      }],
    }),
  });

  if (!resp.ok) throw new Error(`Claude API ${resp.status}`);
  const data = await resp.json();
  const raw  = (data?.content?.[0]?.text ?? '{}').replace(/```json|```/g, '').trim();
  const parsed = JSON.parse(raw);

  // Heuristic: meaning matches if back-translated text is similar to corrected
  const sim = cosineSim(parsed.corrected?.toLowerCase() ?? '', parsed.backTranslated?.toLowerCase() ?? '');

  return {
    original:       text,
    corrected:      parsed.corrected      ?? text,
    translated:     parsed.translated     ?? text,
    backTranslated: parsed.backTranslated ?? '',
    meaningMatch:   sim > 0.6,
  };
}

// Very simple word-overlap similarity for meaning check
function cosineSim(a: string, b: string): number {
  const wa = new Set(a.split(/\s+/));
  const wb = new Set(b.split(/\s+/));
  const inter = [...wa].filter(w => wb.has(w)).length;
  return inter / Math.sqrt(wa.size * wb.size || 1);
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  user:         UserInfo | null;
  messages:     ChatMessage[];
  agent:        Agent;
  apiKey:       string;
  trainingMatches?: TrainingMatch[];
  onSend:       (user: UserInfo, english: string, translated: string, usedLLM: boolean) => void;
  onPlayTraining?: (trainingId: string) => void;
  onSendImage?: (user: UserInfo, text: string, imageB64: string, mimeType: string) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const ChatPanelV2: React.FC<Props> = ({
  user, messages, agent, apiKey, trainingMatches,
  onSend, onPlayTraining, onSendImage,
}) => {
  const [text,         setText]         = useState('');
  const [busy,         setBusy]         = useState(false);
  const [grammarResult,setGrammarResult]= useState<GrammarResult | null>(null);
  const [checking,     setChecking]     = useState(false);
  const [showPreview,  setShowPreview]  = useState(false);
  const [imagePreview, setImagePreview] = useState<{ b64: string; mime: string } | null>(null);
  const [showTraining, setShowTraining] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRef   = useRef<HTMLInputElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { if ((trainingMatches?.length ?? 0) > 0) setShowTraining(true); }, [trainingMatches]);

  const lang = user?.lang ?? 'en';

  // ── Grammar check ───────────────────────────────────────────────────────────
  const handleGrammarCheck = useCallback(async () => {
    const t = text.trim();
    if (!t || !apiKey || !user || checking) return;
    setChecking(true);
    setGrammarResult(null);
    try {
      const result = await grammarAndTranslate(t, lang, apiKey);
      setGrammarResult(result);
      setShowPreview(true);
    } catch { /* show no result */ }
    setChecking(false);
  }, [text, apiKey, user, lang, checking]);

  // ── Send (uses grammar result if available, else translate fresh) ────────────
  const send = useCallback(async () => {
    const t = text.trim();
    if (!t || !user || busy) return;
    setText('');
    setGrammarResult(null);
    setShowPreview(false);
    setImagePreview(null);
    setBusy(true);

    try {
      if (imagePreview && onSendImage) {
        onSendImage(user, t, imagePreview.b64, imagePreview.mime);
      } else if (grammarResult) {
        onSend(user, grammarResult.corrected, grammarResult.translated, true);
      } else {
        const { translated, usedLLM } = await translateMessage(t, lang, apiKey);
        onSend(user, t, translated, usedLLM);
      }
    } finally { setBusy(false); }
  }, [text, user, busy, grammarResult, imagePreview, lang, apiKey, onSend, onSendImage]);

  // ── Accept grammar correction ────────────────────────────────────────────────
  const acceptCorrection = () => {
    if (grammarResult) setText(grammarResult.corrected);
    setShowPreview(false);
    setGrammarResult(null);
  };

  // ── Screenshot ───────────────────────────────────────────────────────────────
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const full = reader.result as string;
      const b64  = full.split(',')[1];
      setImagePreview({ b64, mime: file.type });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  // ── Empty state ──────────────────────────────────────────────────────────────
  if (!user) return (
    <div className={s.panel}>
      <div className={s.header}>
        <div className={s.hTitle}>Conversation</div>
        <div className={s.hSub}>Select a user to chat</div>
      </div>
      <div className={s.empty}>
        <div className={s.emptyIco}>💬</div>
        <div className={s.emptyTxt}>No conversation open</div>
      </div>
    </div>
  );

  return (
    <div className={s.panel}>
      {/* Header */}
      <div className={s.header}>
        <Avatar name={user.name} userId={user.id} size={28}/>
        <div>
          <div className={s.hTitle}>{user.name}</div>
          <div className={s.hSub}>
            {LANG_FLAGS[lang]} {LANG_NAMES[lang]} · {user.currentPage ?? 'Unknown'}
          </div>
        </div>
      </div>

      {/* Idle banner */}
      {(user.idleSeconds ?? 0) >= 60 && (
        <div className={s.idleBanner}>
          <span>⏱</span>
          <span>Idle {Math.floor((user.idleSeconds ?? 0) / 60)}m on <strong>{user.currentPage}</strong></span>
        </div>
      )}

      {/* Training matches */}
      {showTraining && (trainingMatches?.length ?? 0) > 0 && (
        <div style={{
          margin: '6px 8px', padding: '8px 12px',
          background: 'var(--bg-raised)', border: '1px solid var(--br-base)', borderRadius: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--tx-secondary)' }}>
              🎬 {trainingMatches!.length} matching training video{trainingMatches!.length > 1 ? 's' : ''}
            </span>
            <button onClick={() => setShowTraining(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--tx-muted)' }}>✕</button>
          </div>
          {trainingMatches!.map(t => (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0',
              borderBottom: '1px solid var(--br-base)',
            }}>
              <span style={{ fontSize: 14 }}>▶</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-primary)' }}>{t.title}</div>
                <div style={{ fontSize: 10, color: 'var(--tx-muted)' }}>
                  {Math.floor(t.durationSec / 60)}:{String(t.durationSec % 60).padStart(2, '0')} · {t.tags.slice(0, 2).join(', ')}
                </div>
              </div>
              {onPlayTraining && (
                <button onClick={() => onPlayTraining(t.id)}
                  style={{
                    background: 'var(--amber)', color: '#fff', border: 'none',
                    borderRadius: 5, padding: '3px 8px', fontSize: 10, fontWeight: 600, cursor: 'pointer',
                  }}>
                  Play for user
                </button>
              )}
            </div>
          ))}
          <div style={{ marginTop: 8, fontSize: 10, color: 'var(--tx-muted)' }}>
            Or{' '}
            <span style={{ color: 'var(--amber)', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => { /* trigger live chat */ }}>
              connect to live support
            </span>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className={s.messages}>
        {messages.length === 0
          ? <div className={s.empty} style={{ flex: 1 }}>
              <div className={s.emptyIco}>💬</div>
              <div className={s.emptyTxt}>Start the conversation</div>
            </div>
          : messages.map(m => (
              <div key={m.id} className={`${s.wrap} ${s[m.dir]}`}>
                <div className={s.bubble}>
                  {/* Image preview in message */}
                  {(m as any).imageB64 && (
                    <img
                      src={`data:${(m as any).mimeType ?? 'image/png'};base64,${(m as any).imageB64}`}
                      alt="screenshot"
                      style={{ maxWidth: '100%', borderRadius: 6, marginBottom: 6, display: 'block' }}
                    />
                  )}
                  {m.text}
                  {m.translated && m.translated !== m.text && (
                    <div className={s.translation}>
                      {m.usedLLM ? '🤖' : '📖'} {m.translated}
                    </div>
                  )}
                </div>
                <div className={s.meta}>
                  <span className={`${s.tag} ${m.dir === 'out' ? s.tagAgent : m.dir === 'sys' ? s.tagSys : s.tagUser}`}>
                    {m.dir === 'out' ? agent.role : m.dir === 'sys' ? 'System' : m.from}
                  </span>
                  <span>{fmt(m.ts)}</span>
                  {m.usedLLM && <span className={s.tagLLM}>🤖 LLM</span>}
                </div>
              </div>
            ))
        }
        {busy && (
          <div className={s.translating}>
            <span className={s.dots}><span /><span /><span /></span>
            Translating to {LANG_NAMES[lang]}…
          </div>
        )}
        {checking && (
          <div className={s.translating}>
            <span className={s.dots}><span /><span /><span /></span>
            Checking grammar + translating…
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Grammar preview panel */}
      {showPreview && grammarResult && (
        <div style={{
          margin: '0 8px 4px', padding: '10px 12px',
          background: 'var(--bg-raised)', border: '1px solid var(--br-base)', borderRadius: 8,
          fontSize: 11,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontWeight: 700, color: 'var(--tx-secondary)' }}>Grammar &amp; Translation Preview</span>
            <button onClick={() => { setShowPreview(false); setGrammarResult(null); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--tx-muted)' }}>✕</button>
          </div>
          {grammarResult.corrected !== grammarResult.original && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ color: 'var(--tx-muted)', fontSize: 10, marginBottom: 2 }}>GRAMMAR CORRECTED</div>
              <div style={{ color: 'var(--tx-primary)' }}>{grammarResult.corrected}</div>
              <button onClick={acceptCorrection}
                style={{
                  marginTop: 4, background: 'none', border: '1px solid var(--br-base)',
                  borderRadius: 4, padding: '2px 8px', fontSize: 10, cursor: 'pointer',
                  color: 'var(--tx-secondary)',
                }}>
                Use corrected text
              </button>
            </div>
          )}
          <div style={{ marginBottom: 6 }}>
            <div style={{ color: 'var(--tx-muted)', fontSize: 10, marginBottom: 2 }}>
              {LANG_FLAGS[lang]} TRANSLATION TO {(LANG_NAMES[lang] ?? lang).toUpperCase()}
            </div>
            <div style={{ color: 'var(--tx-primary)' }}>{grammarResult.translated}</div>
          </div>
          <div>
            <div style={{ color: 'var(--tx-muted)', fontSize: 10, marginBottom: 2 }}>BACK-TRANSLATED TO ENGLISH</div>
            <div style={{ color: 'var(--tx-primary)' }}>{grammarResult.backTranslated}</div>
            <div style={{
              marginTop: 4, fontSize: 10, fontWeight: 700,
              color: grammarResult.meaningMatch ? '#2E7D32' : '#C62828',
            }}>
              {grammarResult.meaningMatch
                ? '✓ Meaning preserved — safe to send'
                : '⚠ Meaning may differ — review before sending'}
            </div>
          </div>
        </div>
      )}

      {/* Screenshot preview */}
      {imagePreview && (
        <div style={{ margin: '0 8px 4px', position: 'relative', display: 'inline-block' }}>
          <img
            src={`data:${imagePreview.mime};base64,${imagePreview.b64}`}
            alt="attachment"
            style={{ maxHeight: 80, borderRadius: 6, border: '1px solid var(--br-base)' }}
          />
          <button onClick={() => setImagePreview(null)}
            style={{
              position: 'absolute', top: -6, right: -6,
              background: '#C62828', color: '#fff', border: 'none',
              borderRadius: '50%', width: 18, height: 18, cursor: 'pointer', fontSize: 10,
            }}>✕</button>
        </div>
      )}

      {/* Input area */}
      <div className={s.inputWrap}>
        <div className={s.inputBox}>
          <textarea
            rows={2}
            placeholder={`Type in English → ${LANG_NAMES[lang]}${apiKey ? ' (🤖 Claude)' : ' (built-in)'}`}
            value={text}
            onChange={e => { setText(e.target.value); setGrammarResult(null); setShowPreview(false); }}
            onKeyDown={onKey}
          />
          <div className={s.inputFoot}>
            <div className={s.hint}>
              {/* Screenshot button */}
              <button
                title="Attach screenshot"
                onClick={() => fileRef.current?.click()}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 14, padding: '0 4px', color: 'var(--tx-muted)',
                }}
              >📎</button>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onFileChange}/>

              {/* Grammar check button — only when Claude key available */}
              {apiKey && (
                <button
                  disabled={!text.trim() || checking}
                  onClick={handleGrammarCheck}
                  title="Check grammar + preview translation"
                  style={{
                    background: 'none', border: '1px solid var(--br-base)',
                    borderRadius: 4, padding: '2px 6px', fontSize: 10,
                    cursor: text.trim() ? 'pointer' : 'not-allowed',
                    color: 'var(--tx-muted)',
                  }}
                >
                  {checking ? '…' : '✓ Check'}
                </button>
              )}

              <span>{LANG_FLAGS[lang]}</span>
              <span>→ {LANG_NAMES[lang]}</span>
              {apiKey
                ? <span className={s.llmBadge}>🤖 Claude</span>
                : <span className={s.builtinBadge}>📖 built-in</span>}
            </div>
            <button
              className={s.sendBtn}
              disabled={(!text.trim() && !imagePreview) || busy}
              onClick={send}
            >
              {LANG_FLAGS[lang]} Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
